import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { getAllBlogs, getBlogPost, initializeBlogSystem } from '../database/blogStore';
import ReactMarkdown from 'react-markdown';
import { formatDate } from '../utils/dateUtils';
import { getReadingTime, isSvgImage } from '../utils/blogReadingTime';
import { SEOHead } from '../components';
import BlogImage from '../components/BlogImage';
import { resolveBlogImageUrl } from '../utils/blogImage';
import './BlogDetail.css';
import {
    Clock,
    User,
    Calendar,
    ChevronRight,
    Twitter,
    Linkedin,
    Facebook,
    Mail
} from 'lucide-react';

// Get excerpt from content
const getExcerpt = (content, maxLength = 160) => {
    if (!content) return '';
    
    // Remove markdown headers
    const withoutHeaders = content.replace(/^#+ .*$/gm, '').trim();
    
    // Get first paragraph
    const firstParagraph = withoutHeaders.split('\n\n')[0];
    
    if (firstParagraph.length <= maxLength) return firstParagraph;
    
    return firstParagraph.substring(0, maxLength) + '...';
};

const SECTION_BOUNDARY = '(?=\\n## |\\n---|$)';

// Hero renders title; executive summary is omitted from the article body.
const cleanMainContent = (content) => {
    let result = content;
    result = result.replace(/^\s*#\s+.+\n+/, '');
    result = result.replace(new RegExp(`## Executive Summary[\\s\\S]*?${SECTION_BOUNDARY}`), '');
    result = result.replace(/\n{3,}/g, '\n\n');
    return result.trim();
};

const BlogDetail = () => {
    const { identifier } = useParams();
    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [mainContent, setMainContent] = useState('');
    const { theme } = useTheme();

    useEffect(() => {
        const loadPost = async () => {
            try {
                setIsLoading(true);
                await initializeBlogSystem();
                const loadedPost = await getBlogPost(identifier);

                setPost(loadedPost);
                setMainContent(cleanMainContent(loadedPost.content));

                // Fetch related posts based on tags
                if (loadedPost.tags && loadedPost.tags.length > 0) {
                    const allPosts = await getAllBlogs('published');
                    const related = allPosts
                        .filter(p => p.id !== loadedPost.id)
                        .filter(p => p.tags && p.tags.some(tag => loadedPost.tags.includes(tag)))
                        .slice(0, 3);

                    setRelatedPosts(related);
                }
            } catch (error) {
                console.error('Error loading blog post:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadPost();
    }, [identifier]);

    const canonicalSlug = post?.slug || post?.id;
    const canonicalUrl = canonicalSlug
        ? `https://sharedoxygen.com/blog/${canonicalSlug}`
        : window.location.href;
    const shareTitle = post?.title;

    const handleShare = (platform) => {
        const urls = {
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(canonicalUrl)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`,
            email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(canonicalUrl)}`
        };
        window.open(urls[platform], '_blank');
    };

    if (isLoading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className="text-center">
                    <h2 className={`text-2xl mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Blog Post Not Found</h2>
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>The blog post you're looking for doesn't exist or has been removed.</p>
                    <Link to="/blog" className={`mt-4 inline-block ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-primary-600 hover:text-primary-700'}`}>
                        Return to Blog
                    </Link>
                </div>
            </div>
        );
    }

    const readingTime = getReadingTime(post.content);
    const heroImageUrl = resolveBlogImageUrl({
        title: post.title,
        slug: post.slug || post.id,
        imageUrl: post.imageUrl,
    });
    const heroImageClass = isSvgImage(heroImageUrl)
        ? 'w-full h-full object-contain bg-slate-900'
        : 'w-full h-full object-cover';

    // Add dark theme class when dark mode is active
    const bodyClass = theme === 'dark' ? 'dark-theme' : 'light-theme';

    return (
        <div className={`min-h-screen pb-16 ${bodyClass} ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* SEO Meta Tags */}
            <SEOHead
                title={post.title}
                description={post.excerpt || getExcerpt(post.content, 160)}
                image={heroImageUrl}
                imageWidth={isSvgImage(heroImageUrl) ? '1200' : '1200'}
                imageHeight={isSvgImage(heroImageUrl) ? '675' : '630'}
                url={canonicalUrl}
                type="article"
                author="Shared Oxygen"
                publishedTime={post.publishedDate}
                modifiedTime={post.updatedAt}
                tags={post.tags || []}
            />
            
            {/* Hero Section */}
            <div className="blog-hero">
                <img
                    src={heroImageUrl}
                    alt={post.title}
                    className={heroImageClass}
                />
                <div className="blog-hero-content absolute inset-0 p-8 flex flex-col justify-end">
                    <div className="max-w-4xl mx-auto w-full">
                        <nav className="mb-8 flex items-center text-white/80">
                            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                            <ChevronRight className="w-4 h-4 mx-2" />
                            <span className="text-white/60">{post.title}</span>
                        </nav>
                        <div className="flex flex-wrap gap-2 mb-6">
                            {post.tags?.map((tag, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 rounded-full text-sm font-medium bg-slate-900/75 text-white border border-white/25"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <h1 className="blog-hero-title mb-6">{post.title}</h1>
                        <div className="blog-hero-meta flex items-center gap-6 text-sm">
                            <div className="flex items-center">
                                <User className="w-4 h-4 mr-2" />
                                <span className="font-medium">Shared Oxygen</span>
                            </div>
                            <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2" />
                                <span>{formatDate(post.publishedDate || post.createdAt)}</span>
                            </div>
                            <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-2" />
                                <span>{readingTime} min read</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4">
                {/* Main Content */}
                <div className="blog-content">
                    <ReactMarkdown
                        components={{
                            img: ({ alt, src }) => (
                                <figure className="my-10">
                                    <div className="overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5">
                                        <img
                                            src={src}
                                            alt={alt || ''}
                                            className="w-full h-auto"
                                            loading="lazy"
                                        />
                                    </div>
                                    {alt && (
                                        <figcaption className={`mt-3 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {alt}
                                        </figcaption>
                                    )}
                                </figure>
                            ),
                        }}
                    >
                        {mainContent}
                    </ReactMarkdown>
                </div>

                {/* Author Section */}
                {/* <div className="blog-author">
                    <div className="blog-author-header">
                        <img
                            src="/images/admin-avatar.jpg"
                            alt="Shared Oxygen"
                            className="blog-author-avatar"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/images/default-avatar.jpg';
                            }}
                        />
                        <div>
                            <h3 className="blog-author-name">Shared Oxygen</h3>
                        </div>
                    </div>
                    <p className="blog-author-bio">
                        Shared Oxygen helps executives put AI and data under governance,
                        measurement, and operating control.
                    </p>
                </div> */}

                {/* Share Section */}
                <div className="blog-share">
                    <div className="flex flex-col items-center">
                        <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Share This Article</h3>
                        <div className="flex gap-4">
                            <button
                                onClick={() => handleShare('twitter')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share on Twitter"
                            >
                                <Twitter className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleShare('linkedin')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share on LinkedIn"
                            >
                                <Linkedin className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleShare('facebook')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share on Facebook"
                            >
                                <Facebook className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleShare('email')}
                                className={`p-2 rounded-full transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                aria-label="Share via Email"
                            >
                                <Mail className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
                <div className="blog-related">
                    <h2 className="blog-related-title">Related Articles</h2>
                    <div className="blog-related-grid">
                        {relatedPosts.map(relatedPost => (
                            <Link
                                key={relatedPost.id}
                                to={`/blog/${relatedPost.slug || relatedPost.id}`}
                                className={`rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all group ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                                    }`}
                            >
                                <div className="aspect-video relative overflow-hidden bg-slate-900">
                                    <BlogImage
                                        title={relatedPost.title}
                                        slug={relatedPost.slug || relatedPost.id}
                                        imageUrl={relatedPost.imageUrl}
                                        size="medium"
                                        className="h-full w-full transition-transform duration-300 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                </div>
                                <div className="p-4">
                                    <h3 className={`font-bold mb-2 transition-colors ${theme === 'dark'
                                            ? 'text-white group-hover:text-blue-300'
                                            : 'text-gray-800 group-hover:text-blue-600'
                                        }`}>
                                        {relatedPost.title}
                                    </h3>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {formatDate(relatedPost.publishedDate || relatedPost.createdAt)}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogDetail; 